/**
 * ViewSwitcher — tab bar for switching between Map, List, Matrix, and Dashboard views.
 * Syncs active view with URL hash for persistence across refreshes.
 */

import { useEffect } from 'react';
import { useAdjacencyStore } from '../../store/adjacency-store';
import type { ViewMode } from '../../types/adjacency';
import { Map, List, LayoutGrid, BarChart3, DollarSign, Users, Clock } from 'lucide-react';

const VIEW_TABS: { mode: ViewMode; label: string; icon: typeof Map }[] = [
  { mode: 'map', label: 'Map', icon: Map },
  { mode: 'list', label: 'List', icon: List },
  { mode: 'matrix', label: 'Matrix', icon: LayoutGrid },
  { mode: 'dashboard', label: 'Map Metrics', icon: BarChart3 },
  { mode: 'finance', label: 'Finance', icon: DollarSign },
  { mode: 'resources', label: 'Resources', icon: Users },
  { mode: 'timeline', label: 'Timeline', icon: Clock },
];

const VALID_MODES = new Set<string>(VIEW_TABS.map((t) => t.mode));

function parseHash(): ViewMode | null {
  const raw = window.location.hash.replace('#', '');
  return VALID_MODES.has(raw) ? (raw as ViewMode) : null;
}

export function ViewSwitcher() {
  const viewMode = useAdjacencyStore((s) => s.viewMode);
  const setViewMode = useAdjacencyStore((s) => s.setViewMode);

  // Initialize from hash on mount
  useEffect(() => {
    const fromHash = parseHash();
    if (fromHash && fromHash !== viewMode) {
      setViewMode(fromHash);
    } else if (!fromHash) {
      window.location.hash = viewMode;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Listen for external hashchange (back/forward nav)
  useEffect(() => {
    const onHashChange = () => {
      const mode = parseHash();
      if (mode && mode !== useAdjacencyStore.getState().viewMode) {
        setViewMode(mode);
      }
    };
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, [setViewMode]);

  const handleTabClick = (mode: ViewMode) => {
    setViewMode(mode);
    window.location.hash = mode;
  };

  return (
    <nav className="glass-subtle px-6 border-b" style={{ borderColor: 'var(--glass-border)' }}>
      <div className="flex gap-1">
        {VIEW_TABS.map(({ mode, label, icon: Icon }) => (
          <button
            key={mode}
            onClick={() => handleTabClick(mode)}
            className="flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors"
            style={{
              borderColor: viewMode === mode ? 'var(--tab-active-border)' : 'transparent',
              color: viewMode === mode ? 'var(--tab-active-color)' : 'var(--tab-inactive-color)',
              ...(viewMode === mode ? { textShadow: 'var(--tab-active-glow)' } : {}),
            }}
          >
            <Icon size={16} />
            {label}
          </button>
        ))}
      </div>
    </nav>
  );
}
